import classes from "./Services.module.css";
import { useMemo, useState } from "react";
import { ServiceModal } from "../CreativeEditing/ServiceModal";

export const Services = ({ creative }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Transform packages from creative data to match the expected structure
  const servicesArray = useMemo(() => {
    if (!creative?.packages?.items) return [];
    
    return creative.packages.items
      .filter(pkg => pkg.status === 'PUBLISHED' && pkg.isActive !== false)
      .map(pkg => {
        // Determine type: check if packageType or serviceType indicates "JobsInStudio"
        // Otherwise default to "Pakete"
        const packageTypeStr = String(pkg.packageType || '').toUpperCase();
        const serviceTypeStr = String(pkg.serviceType || '').toUpperCase();
        const isJobsInStudio = packageTypeStr.includes('JOBS') || 
                             packageTypeStr.includes('STUDIO') ||
                             serviceTypeStr.includes('JOBS') || 
                             serviceTypeStr.includes('STUDIO');
        
        return {
          id: pkg.id,
          type: isJobsInStudio ? 'JobsInStudio' : 'Pakete',
          name: pkg.name || '',
          price: pkg.pricePerHour || pkg.price || 0,
          description: pkg.description || '',
          image: pkg.processedPortfolioImages?.[0] || null,
          extras: pkg.deliverables?.[0] || pkg.includedServices?.[0] || ''
        };
      });
  }, [creative?.packages?.items]);

    // Separate services by type and prepare two columns
    const jobsInStudio = servicesArray.filter((s) => s.type === 'JobsInStudio');
    const pakete = servicesArray.filter((s) => s.type === 'Pakete');

    const shouldRenderJobsColumn = jobsInStudio.length > 0;
    const shouldRenderPaketeColumn = pakete.length > 0;

  const handleServiceClick = (serviceId) => {
    // Find the full package object from creative.packages.items
    const fullPackage = creative?.packages?.items?.find(pkg => pkg.id === serviceId);
    if (fullPackage) {
      setSelectedService(fullPackage);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };
  
  return (
    <>
      <div className={classes.arbeitenContainer}>
        <h2 className="titleSemibold3">Services</h2>
        <div className={classes.servicesColumns}>
          {shouldRenderJobsColumn && (
            <div className={classes.serviceColumn}>
              <h3 className="titleSemibold3">Arbeitszeit im Studio + Rohdateien </h3>
              <div className={classes.servicesContent}>
                {jobsInStudio?.map((service) => (
                  <div 
                    className={classes.serviceItem} 
                    key={`jobs-${service.id}`}
                    onClick={() => handleServiceClick(service.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={classes.serviceItemImage}>
                      {service?.image && <img src={service.image} alt={service.name} />}
                    </div>
                    <div className={classes.serviceItemText}>
                      <span className="semiboldMiddle">{service.name}</span>
                      <span className="regularMiddle">{service.price} € pro Stunde</span>
                      <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                      <span className={`regularMiddle ${classes.serviceDescription}`}>{service.description}</span>
                      <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                          <g clipPath="url(#clip0_15597_28902)">
                          <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          </g>
                          <defs>
                          <clipPath id="clip0_15597_28902">
                          <rect width="21.8118" height="15.0954" fill="white"/>
                          </clipPath>
                          </defs>
                        </svg>
                        <span className="regularXss">{service.extras}</span>
                      </div>
                    </div>
                    <div className={classes.serviceItemTextPhone}>
                      <span className="semiboldSmall">{service.name}</span>
                      <span className="regularSmall">{service.price} € pro Stunde</span>
                      <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="slimRegularMiniText">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                      {service.extras && (
                      <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                          <g clipPath="url(#clip0_15597_28902)">
                          <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          </g>
                          <defs>
                          <clipPath id="clip0_15597_28902">
                          <rect width="21.8118" height="15.0954" fill="white"/>
                          </clipPath>
                          </defs>
                        </svg>
                        <span className="regularXs">{service.extras}</span>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {shouldRenderPaketeColumn && (
            <div className={classes.serviceColumn}>
              <h3 className="titleSemibold3">Pakete</h3>
              <div className={classes.servicesContent}>
                {pakete.map((service) => (
                  <div 
                    className={classes.serviceItem} 
                    key={`pakete-${service.id}`}
                    onClick={() => handleServiceClick(service.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={classes.serviceItemImage}>
                      {service?.image && <img src={service.image} alt={service.name} />}
                    </div>
                    <div className={classes.serviceItemText}>
                      <span className="semiboldMiddle">{service.name}</span>
                      <span className="regularMiddle">{service.price} € pro Stunde</span>
                      <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                      <span className={`regularMiddle ${classes.serviceDescription}`}>{service.description}</span>
                      {service.extras && (
                      <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                          <g clipPath="url(#clip0_15597_28902)">
                          <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          </g>
                          <defs>
                          <clipPath id="clip0_15597_28902">
                          <rect width="21.8118" height="15.0954" fill="white"/>
                          </clipPath>
                          </defs>
                        </svg>
                        <span className="regularXss">{service.extras}</span>
                      </div>
                      )}
                    </div>
                    <div className={classes.serviceItemTextPhone}>
                      <span className="semiboldSmall">{service.name}</span>
                      <span className="regularSmall">{service.price} € pro Stunde</span>
                      <span style={{color: "var(--dark-grey-4)" , marginBottom: "12px"}} className="slimRegularMiniText">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                      {service.extras && (
                      <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                          <g clipPath="url(#clip0_15597_28902)">
                          <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                          </g>
                          <defs>
                          <clipPath id="clip0_15597_28902">
                          <rect width="21.8118" height="15.0954" fill="white"/>
                          </clipPath>
                          </defs>
                        </svg>
                        <span className="regularXs">{service.extras}</span>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {isModalOpen && selectedService && (
        <ServiceModal
          open={isModalOpen}
          onClose={handleCloseModal}
          service={selectedService}
          creative={creative}
          servicesArray={creative?.packages?.items}
          hideEditButton={true}
        />
      )}
    </>
  );
};

export default Services;